/**
 * Event Builder — derived values for the shell and sections (plan 6.B / 6.C).
 * Pure functions over the reducer state; no side effects.
 */

import moment from "moment";
import { isTwentyFourHourFormat } from "../../../../utils/eventConfigHelpers";
import {
  ASSIGNMENT_MODE,
  COPY,
  DEFAULT_CURRENCY,
  E,
  FORMAT,
  KIND,
  PAYMENT_TYPE,
  SCHEDULE_TYPE,
  SECTION_ORDER,
  SECTIONS,
  STEP_TO_NAV_SECTION,
  VENUE_NAMES,
  WEEKDAYS,
} from "./constants";
import { anyDirty, isStepDirty } from "./builderReducer";

// ---- kind / format ------------------------------------------------------------------------------------

export const isPaid = (state) => state.meta.kind === KIND.PAID;
export const isGroup = (state) => String(state.type.event_format_id) === FORMAT.GROUP;
export const formatLabel = (state) => (isGroup(state) ? "Group" : "One-to-one");

// ---- duration ------------------------------------------------------------------------------------------

export const inviteeChoosesDuration = (state) => state.availability.invitee_set_duration_status === E;
export const durationMinutes = (state) => parseInt(state.availability.event_duration_in_minutes, 10) || 0;
export const durationLabel = (state) => (inviteeChoosesDuration(state) ? "Custom" : `${durationMinutes(state)} min`);
export const durationLongLabel = (state) =>
  inviteeChoosesDuration(state) ? "Invitee chooses" : `${durationMinutes(state)} minutes`;

// ---- price ----------------------------------------------------------------------------------------------

export const currency = (state) => state.lookups.paymentCurrency || DEFAULT_CURRENCY;
export const price = (state) => {
  const first = ((state.details && state.details.pricing) || [])[0] || {};
  const n = Number(first.price);
  return Number.isFinite(n) && first.price !== "" ? n : null;
};
export const priceLabel = (state) => {
  const p = price(state);
  return p === null ? "" : `${p} ${currency(state)}`;
};

/** Smart-defaults paid row small text (6.C). */
export const paymentTimingLabel = (state) => {
  const d = state.details || {};
  if (d.event_prepaid_status !== E) return "Pay at event";
  if (d.deposit_type === PAYMENT_TYPE.DEPOSIT) return `Deposit \u00B7 ${d.deposit_price || 0}% now`;
  if (d.payments_type === PAYMENT_TYPE.PARTIAL) return "Partial payment";
  return "Full payment";
};

// ---- summary line (sidebar) -----------------------------------------------------------------------------

export const summaryLine = (state) => {
  const parts = [durationLabel(state), formatLabel(state)];
  if (isPaid(state) && price(state) !== null) parts.push(priceLabel(state));
  return parts.join(" \u00B7 ");
};

export const eventDisplayName = (state) => (state.type.event_name || "").trim() || COPY.sidebar.newEvent;

// ---- location ---------------------------------------------------------------------------------------------

export const venueName = (state) => VENUE_NAMES[parseInt(state.type.venue_id, 10)] || "";

// ---- schedule summary (Quick setup block 5) -------------------------------------------------------------

const to12h = (hhmm) => {
  const m = moment(hhmm, "HH:mm", true);
  return m.isValid() ? m.format("h:mm A") : hhmm;
};

export const formatTime = (hhmm, timeFormat) => {
  if (!hhmm) return "";
  return isTwentyFourHourFormat(timeFormat) ? hhmm : to12h(hhmm);
};

export const eventScheduleRows = (state) => {
  const row = (state.availability.schedules || []).find((s) => s.type === SCHEDULE_TYPE.EVENT);
  return row ? row.schedules || [] : [];
};

export const weeklyRows = (state) => eventScheduleRows(state).filter((r) => r.dayId !== null && r.dayId !== undefined);
export const customDateRows = (state) => eventScheduleRows(state).filter((r) => r.dated);

/** `Mon–Fri`, `Mon, Wed, Fri`, `Every day`, `No days` */
export const dayRangeLabel = (state) => {
  const ids = weeklyRows(state)
    .filter((r) => (r.schedules || []).length)
    .map((r) => r.dayId)
    .sort((a, b) => a - b);
  if (!ids.length) return "No days";
  if (ids.length === 7) return "Every day";
  const short = (id) => (WEEKDAYS.find((w) => w.id === id) || {}).short || "";
  const consecutive = ids.every((id, i) => i === 0 || id === ids[i - 1] + 1);
  if (consecutive && ids.length > 2) return `${short(ids[0])}\u2013${short(ids[ids.length - 1])}`;
  return ids.map(short).join(", ");
};

/** `9:00 AM–5:00 PM`, or `Varies` when the days do not share the same single window. */
export const hoursLabel = (state, timeFormat) => {
  const rows = weeklyRows(state).filter((r) => (r.schedules || []).length);
  if (!rows.length) return "";
  const signature = (r) => (r.schedules || []).map((s) => `${s.start_time}-${s.end_time}`).join("|");
  const first = signature(rows[0]);
  const uniform = rows.every((r) => signature(r) === first);
  if (!uniform || rows[0].schedules.length !== 1) return "Varies";
  const { start_time, end_time } = rows[0].schedules[0];
  return `${formatTime(start_time, timeFormat)}\u2013${formatTime(end_time, timeFormat)}`;
};

export const timezoneLabel = (state) => {
  const id = String(state.availability.timezone_id || "");
  const tz = (state.lookups.timezones || []).find((t) => String(t.value) === id);
  return tz ? tz.label : "";
};

export const selectedTemplate = (state) => {
  const id = String(state.availability.templateId || "");
  return (state.lookups.scheduleTemplates || []).find((t) => String(t.value) === id) || null;
};

// ---- hosts ------------------------------------------------------------------------------------------------

export const assignmentMode = (state) => state.availability.assignmentMode || ASSIGNMENT_MODE.MY;

export const selectedStaff = (state) => {
  const ids = (state.availability.staffs || []).map((n) => parseInt(n, 10));
  const active = state.lookups.staff || [];
  const fromEvent = state.lookups.eventStaffs || [];
  return ids.map((id) => {
    const s = active.find((x) => parseInt(x.id, 10) === id) || fromEvent.find((x) => parseInt(x.id, 10) === id);
    return s
      ? { id, name: `${s.first_name || ""} ${s.last_name || ""}`.trim(), email: s.email || "", inactive: !active.some((x) => parseInt(x.id, 10) === id) }
      : { id, name: `Staff #${id}`, email: "", inactive: true };
  });
};

// ---- save / footer -----------------------------------------------------------------------------------------

export const isSaving = (state) => state.save.status === "saving";
export const hasUnsaved = (state) => anyDirty(state.save) || isSaving(state);

export const savedStateText = (state) => {
  const { status } = state.save;
  if (status === "saving") return COPY.topbar.saving;
  if (status === "error") return COPY.topbar.error;
  if (!state.meta.eventId) return COPY.topbar.unsaved;
  if (anyDirty(state.save)) return COPY.topbar.unsavedChanges;
  return COPY.topbar.saved;
};

export const savedStateClass = (state) => {
  const { status } = state.save;
  if (status === "saving") return "is-saving";
  if (status === "error") return "is-error";
  if (!state.meta.eventId || anyDirty(state.save)) return "is-unsaved";
  return "";
};

export const draftLabel = (state) => {
  if (!state.meta.isNew) return COPY.topbar.editing(state.type.event_name);
  return isPaid(state) ? COPY.topbar.newPaid : COPY.topbar.newFree;
};

export const topCreateLabel = (state) => (state.meta.isDraft === E ? COPY.topbar.create : COPY.topbar.save);

export const footerButtonLabel = (state) => {
  if (state.meta.isDraft !== E) return COPY.footer.savePublished;
  return isPaid(state) ? COPY.footer.createPaid : COPY.footer.createFree;
};

export const footerStatusText = (issueCount) =>
  issueCount === 0 ? COPY.footer.complete : COPY.footer.remaining(issueCount);

/** Nav rows that show a red dot: sections with client issues or with server field errors. */
export const sectionsWithErrors = (state, issues) => {
  const set = new Set();
  (issues || []).forEach((i) => set.add(i.section));
  Object.keys(state.save.fieldErrors || {}).forEach((step) => {
    if (Object.keys(state.save.fieldErrors[step] || {}).length) set.add(STEP_TO_NAV_SECTION[step]);
  });
  Object.values(state.save.sectionErrors || {}).forEach((v) => {
    if (v && v.section) set.add(v.section);
  });
  return set;
};

/** Server (non-field) message for a nav section, if any. */
export const sectionErrorMessage = (state, section) => {
  const hit = Object.values(state.save.sectionErrors || {}).find((v) => v && v.section === section);
  return hit ? hit.message : null;
};

export const visibleSections = (state) => SECTION_ORDER.filter((s) => s !== SECTIONS.PAYMENTS || isPaid(state));

export const essentialsCount = (state) => (isPaid(state) ? 6 : 5);

/** `0` → "", 90 → "1.5 hours", 60 → "1 hour", 45 → "45 minutes", 2880 → "2 days". */
export const humanizeMinutes = (value) => {
  const m = parseInt(value, 10) || 0;
  if (m <= 0) return "";
  if (m % 1440 === 0) {
    const d = m / 1440;
    return `${d} ${d === 1 ? "day" : "days"}`;
  }
  if (m % 60 === 0) {
    const h = m / 60;
    return `${h} ${h === 1 ? "hour" : "hours"}`;
  }
  if (m > 60) {
    const h = Math.round((m / 60) * 10) / 10;
    return `${h} hours`;
  }
  return `${m} ${m === 1 ? "minute" : "minutes"}`;
};

export const stepDirty = (state, step) => isStepDirty(state.save, step);

// ---- booking link -------------------------------------------------------------------------------------------

export const bookingPath = (businessId, eventUrl) => `booking/${businessId}/${eventUrl}`;
